import { defaultError } from "@-/com.npmjs.react-context-default-error";
import { createContext, use } from "react";
import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import type { NavAccountDrawerNotifications } from "#use-nav-account-drawer-notifications";

export interface NavAccountDrawerContextValue {
  closeDrawer: () => void;
  drawerProgress: SharedValue<number>;
  drawerWidth: number;
  /** Any row's notification dot is lit; the drawer trigger shows its own dot from this. */
  hasNotifications: boolean;
  isOpen: boolean;
  notifications: NavAccountDrawerNotifications;
  openDrawer: () => void;
}

/**
 * Open/close control for the account drawer.
 *
 * The drawer is a plain overlay (`react-native-drawer-layout`), NOT a navigator — see
 * `NavAccountDrawer` for why a `createDrawerNavigator` here broke every route into `(app)`. So its
 * open state cannot be read through `useNavigation()`; descendants use this context instead.
 * Always provided by `NavAccountDrawer`, which every reader renders under.
 */
export const NavAccountDrawerContext = createContext(
  defaultError<NavAccountDrawerContextValue>("NavAccountDrawerContext"),
);

export function useNavAccountDrawer(): NavAccountDrawerContextValue {
  return use(NavAccountDrawerContext);
}

// Navigation rows do not read open state. Keep their subscriptions independent so pressing the
// trigger only updates the controls and live progress, rather than rebuilding every destination.
export const NavAccountDrawerCloseContext = createContext(
  defaultError<() => void>("NavAccountDrawerCloseContext"),
);
export const NavAccountDrawerNotificationsContext = createContext(
  defaultError<NavAccountDrawerNotifications>("NavAccountDrawerNotificationsContext"),
);

export function useNavAccountDrawerContent() {
  const closeDrawer = use(NavAccountDrawerCloseContext);
  const notifications = use(NavAccountDrawerNotificationsContext);
  return { closeDrawer, notifications };
}

/**
 * Whether the account panel is showing: the native drawer, or web's account popover
 * (`NavAccountDrawerPopover`). The profile surface's live stats (rank, streak, wallet and cards
 * learned today) read only while it is. Wrapped in an object because `defaultError` cannot stand
 * in for a boolean.
 */
export const NavAccountDrawerVisibilityContext = createContext(
  defaultError<{ isOpen: boolean }>("NavAccountDrawerVisibilityContext"),
);

export function useIsNavAccountDrawerOpen(): boolean {
  return use(NavAccountDrawerVisibilityContext).isOpen;
}
